import React, { useState } from 'react';
import BarGraph from '../GraphTypes/BarGraph';
import Loader from '../../General/Loader';
import cn from 'classnames';
import { Table } from '../GraphTypes/Table';

import { getBrandReportHeaderLabel } from '../../../helpers/brand_helpers';
import PropTypes from 'prop-types';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const BrandVolume = props => {
  const { title, brandOrders: volume, downloading } = props;
  const success = !!volume?.length;

  const [tab, setTab] = useState('table');
  const download_table = async () => await props.download({ rows: make_table_rows(), fileName: 'brand_volume' });
  const download_graph = async () => await props.download({ graph: title });

  const make_table_rows = () => {
    let total_running_total = 0;
    const total_clicks = {
      key: 'volume_total',
      label: 'Total Volume',
      description: '(volume to retail site + DTC site)',
      values: volume.map(order => {
        const { week, month, quarter, total_volume } = order;
        total_running_total += parseInt(total_volume || '0');
        return { label: getBrandReportHeaderLabel({ week, month, quarter }), value: '$' + parseInt(total_volume) };
      })
    };
    total_clicks.values.push({ label: 'Total', value: '$' + total_running_total });

    let dtc_running_total = 0;
    const dtc_clicks = {
      key: 'volume_dtc',
      label: 'Total Volume DTC',
      description: `(order volume to dtc site)`,
      values: volume.map(order => {
        const { week, month, quarter, total_volume_dtc } = order;
        dtc_running_total += parseInt(total_volume_dtc || '0');
        return { label: getBrandReportHeaderLabel({ week, month, quarter }), value: '$' + parseInt(total_volume_dtc) };
      })
    };
    dtc_clicks.values.push({ label: 'Total', value: '$' + dtc_running_total });

    let retailer_running_total = 0;
    const retailers_clicks = {
      key: 'volume_retailers_total',
      label: 'Total Volume to Retailers',
      description: `(order volume to retailers of the brand)`,
      values: volume.map(order => {
        const { week, month, quarter, total_volume, total_volume_dtc } = order;
        retailer_running_total += parseInt(total_volume - total_volume_dtc || '0');
        return { label: getBrandReportHeaderLabel({ week, month, quarter }), value: '$' + parseInt(total_volume - total_volume_dtc) };
      })
    };
    retailers_clicks.values.push({ label: 'Total', value: '$' + retailer_running_total });

    const difference = {
      key: 'volume_diff_total',
      label: 'Share of Orders Going Direct',
      description: '(total volume dtc / total volume overall)',
      values: volume.map(order => {
        const { week, month, quarter, total_volume, total_volume_dtc } = order;
        return {
          label: getBrandReportHeaderLabel({ week, month, quarter }),
          value: (parseInt(total_volume_dtc) / parseInt(total_volume)) * 100,
          isPercent: true
        };
      })
    };
    difference.values.push({ label: 'Total', value: `${((parseInt(dtc_running_total) / parseInt(total_running_total)) * 100).toFixed()}%` });

    let dtc_is_same_as_total = true;
    for (let i = 0; i < total_clicks.values.length; ++i) {
      const total = total_clicks.values[i].value,
        dtc = dtc_clicks.values[i].value;
      dtc_is_same_as_total = dtc_is_same_as_total && total === dtc;
    }

    return dtc_is_same_as_total ? [total_clicks] : [total_clicks, dtc_clicks, retailers_clicks, difference];
  };

  return (
    <div className='brand-card' id={title}>
      <div className='padding'>
        {!volume ? (
          <Loader />
        ) : (
          <>
            {success && (
              <>
                <div className='header'>
                  <div className='description'>
                    <div className='title'>{title}</div>
                    <div className='sub-title'>Volume is the amount of money through orders that the brand received.</div>
                  </div>
                  {!downloading && (
                    <div className='brand-card-controls'>
                      <div className='radio-btns'>
                        <div className={cn('radio-btn', { active: tab === 'graph' })} onClick={() => setTab('graph')}>
                          Graph
                        </div>
                        <div className={cn('radio-btn', { active: tab === 'table' })} onClick={() => setTab('table')}>
                          Table
                        </div>
                      </div>
                      <div className='rounded-button' onClick={() => (tab === 'table' ? download_table() : download_graph())}>
                        {downloading ? (
                          <Loader />
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faDownload}></FontAwesomeIcon>
                            Download
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {tab === 'table' && (
                  <Table
                    title='Brand Volume'
                    data={make_table_rows()}
                    // data={[ { } ]}
                  />
                )}
                {tab === 'graph' && (
                  <BarGraph
                    title='Total Order Volume'
                    data={volume.map(click => {
                      const { week, month, quarter, year, total_volume } = click;

                      const data = {
                        key: `${week || month || quarter}${year}ordervolume`,
                        value: parseInt(total_volume),
                        label: getBrandReportHeaderLabel({ month, quarter })
                      };
                      return data;
                    })}
                  />
                )}
              </>
            )}

            {!success && (
              <>
                <div className='error-centered'>
                  <div className='title'>Error Fetching {title}</div>
                  <div className='sub-title'>Could not fetch results</div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

BrandVolume.propTypes = {
  title: PropTypes.string.isRequired,
  brandOrders: PropTypes.array,

  download: PropTypes.func.isRequired,
  downloading: PropTypes.bool.isRequired
};

const brandVolumeConfig = {
  title: 'Volume'
};

export { BrandVolume, brandVolumeConfig };
